import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Wallet, CheckCircle, AlertCircle, DollarSign, Percent, Clock, ArrowDownCircle } from 'lucide-react';
import { toast } from 'sonner';

// Payment method icons
const PayPalIcon = () => <span className="text-[#003087] font-bold">Pay<span className="text-[#009cde]">Pal</span></span>;
const CashAppIcon = () => <span className="text-[#00D632] font-bold">$Cash</span>;
const VenmoIcon = () => <span className="text-[#3D95CE] font-bold">Venmo</span>;

export default function WithdrawPage() {
  const { user, updateUser } = useAuth();
  const { formatCurrency } = useCurrency();
  const [selectedMethod, setSelectedMethod] = useState('venmo');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [withdrawalStatus, setWithdrawalStatus] = useState(null);
  
  // Payment identifiers
  const [paypalEmail, setPaypalEmail] = useState(user?.email || '');
  const [cashtag, setCashtag] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  
  // Fee calculation
  const [feeInfo, setFeeInfo] = useState(null);

  const maxWithdraw = Math.min(user?.balance || 0, 5000);

  // Calculate fees
  useEffect(() => {
    if (amount && parseFloat(amount) >= 20) {
      const amt = parseFloat(amount);
      const feePercent = 7; // 7% withdrawal fee
      const fee = Math.round(amt * feePercent) / 100;
      const netAmount = amt - fee;
      setFeeInfo({
        amount: amt,
        fee,
        fee_percent: feePercent,
        net_amount: netAmount
      });
    } else {
      setFeeInfo(null);
    }
  }, [amount]);

  // PayPal Withdrawal
  const handlePayPalWithdraw = async () => {
    const withdrawAmount = parseFloat(amount);
    if (!withdrawAmount || withdrawAmount < 20) {
      toast.error('Minimum withdrawal is $20');
      return;
    }
    if (withdrawAmount > maxWithdraw) {
      toast.error(`Maximum withdrawal is $${maxWithdraw.toFixed(2)}`);
      return;
    }
    if (!paypalEmail || !paypalEmail.includes('@')) {
      toast.error('Please enter a valid PayPal email');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/paypal/withdraw', {
        amount: withdrawAmount,
        paypal_email: paypalEmail,
        speed: 'standard'
      });
      if (res.data.success) {
        setWithdrawalStatus({ success: true, method: 'paypal', ...res.data });
        const userRes = await api.get('/auth/me');
        updateUser(userRes.data);
        toast.success('Withdrawal request submitted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  // Cash App Withdrawal
  const handleCashAppWithdraw = async () => {
    const withdrawAmount = parseFloat(amount);
    if (!withdrawAmount || withdrawAmount < 20) {
      toast.error('Minimum withdrawal is $20');
      return;
    }
    if (withdrawAmount > maxWithdraw) {
      toast.error(`Maximum withdrawal is $${maxWithdraw.toFixed(2)}`);
      return;
    }
    if (!cashtag || !cashtag.startsWith('$')) {
      toast.error('Please enter a valid Cash App $cashtag (must start with $)');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/payments/cashapp/withdraw', {
        cashtag,
        amount: withdrawAmount
      });
      if (res.data.success) {
        setWithdrawalStatus({ success: true, method: 'cashapp', ...res.data });
        const userRes = await api.get('/auth/me');
        updateUser(userRes.data);
        toast.success('Withdrawal request submitted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  // Venmo Withdrawal
  const handleVenmoWithdraw = async () => {
    const withdrawAmount = parseFloat(amount);
    if (!withdrawAmount || withdrawAmount < 20) {
      toast.error('Minimum withdrawal is $20');
      return;
    }
    if (withdrawAmount > maxWithdraw) {
      toast.error(`Maximum withdrawal is $${maxWithdraw.toFixed(2)}`);
      return;
    }
    if (!venmoUsername || !venmoUsername.startsWith('@')) {
      toast.error('Please enter a valid Venmo username (must start with @)');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/payments/venmo/withdraw', {
        venmo_username: venmoUsername,
        amount: withdrawAmount
      });
      if (res.data.success) {
        setWithdrawalStatus({ success: true, method: 'venmo', ...res.data });
        const userRes = await api.get('/auth/me');
        updateUser(userRes.data);
        toast.success('Withdrawal request submitted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = () => {
    switch (selectedMethod) {
      case 'paypal':
        handlePayPalWithdraw();
        break;
      case 'cashapp':
        handleCashAppWithdraw();
        break;
      case 'venmo':
        handleVenmoWithdraw();
        break;
      default:
        toast.error('Please select a withdrawal method');
    }
  };

  // Withdrawal Success UI
  if (withdrawalStatus) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-green-500">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Withdrawal Submitted!</h2>
              <p className="text-3xl font-bold text-green-500 mb-4">
                ${withdrawalStatus.net_amount?.toFixed(2) || withdrawalStatus.amount}
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Method:</span>
                  <span className="font-medium capitalize">{withdrawalStatus.method}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Destination:</span>
                  <span className="font-medium">
                    {withdrawalStatus.method === 'paypal' && withdrawalStatus.paypal_email}
                    {withdrawalStatus.method === 'cashapp' && withdrawalStatus.cashtag}
                    {withdrawalStatus.method === 'venmo' && withdrawalStatus.venmo_username}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
                    {withdrawalStatus.status || 'Processing'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Time:</span>
                  <span>{withdrawalStatus.estimated_time || 'Within 24 hours'}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                You will receive a confirmation once the funds have been sent.
              </p>
              <Button onClick={() => setWithdrawalStatus(null)}>
                Make Another Withdrawal
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Withdraw Funds</h1>
            <p className="text-muted-foreground">Transfer money to your external account</p>
          </div>
          <Card className="px-4 py-2">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Available:</span>
              <span className="font-bold text-green-500">{formatCurrency(user?.balance || 0)}</span>
            </div>
          </Card>
        </div>

        {/* Balance Check */}
        {(user?.balance || 0) < 20 && (
          <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-medium">Insufficient Balance</p>
                <p className="text-sm text-muted-foreground">
                  Minimum withdrawal is $20. Your current balance is {formatCurrency(user?.balance || 0)}.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Withdrawal Method Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowDownCircle className="w-5 h-5" />
              Select Withdrawal Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedMethod} onValueChange={setSelectedMethod}>
              <TabsList className="grid grid-cols-3 w-full mb-6">
                <TabsTrigger value="cashapp" className="flex items-center gap-2" data-testid="cashapp-withdraw-tab">
                  <CashAppIcon />
                </TabsTrigger>
                <TabsTrigger value="venmo" className="flex items-center gap-2" data-testid="venmo-withdraw-tab">
                  <VenmoIcon />
                </TabsTrigger>
              </TabsList>


              {/* Cash App Tab */}
              <TabsContent value="cashapp" className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex items-center gap-3">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">Cash App Withdrawal</p>
                    <p className="text-sm text-muted-foreground">Funds sent within 24 hours</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Cash App $cashtag</label>
                  <Input
                    placeholder="$YourCashtag"
                    value={cashtag}
                    onChange={(e) => setCashtag(e.target.value)}
                    data-testid="cashtag-withdraw-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must start with $ (e.g., $JohnDoe)</p>
                </div>
              </TabsContent>

              {/* Venmo Tab */}
              <TabsContent value="venmo" className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex items-center gap-3">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">Venmo Withdrawal</p>
                    <p className="text-sm text-muted-foreground">Funds sent within 24 hours</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Venmo Username</label>
                  <Input
                    placeholder="@YourUsername"
                    value={venmoUsername}
                    onChange={(e) => setVenmoUsername(e.target.value)}
                    data-testid="venmo-withdraw-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must start with @ (e.g., @JaneDoe)</p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Amount Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Withdrawal Amount</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {[20, 50, 100, 250, 500, 1000, 2500, 5000].filter(a => a <= maxWithdraw).map((preset) => (
                <Button
                  key={preset}
                  variant={parseFloat(amount) === preset ? 'default' : 'outline'}
                  onClick={() => setAmount(preset.toString())}
                  disabled={preset > (user?.balance || 0)}
                  data-testid={`withdraw-amount-${preset}`}
                >
                  ${preset}
                </Button>
              ))}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">Custom Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-8"
                    min="20"
                    max={maxWithdraw}
                    data-testid="custom-withdraw-input"
                  />
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setAmount(maxWithdraw.toString())}
                className="mt-6"
              >
                Max (${maxWithdraw.toFixed(2)})
              </Button>
            </div>

            {/* Fee Display */}
            {feeInfo && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Withdrawal Amount:</span>
                  <span>${feeInfo.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-yellow-600">
                  <span className="flex items-center gap-1">
                    <Percent className="w-4 h-4" />
                    Processing Fee ({feeInfo.fee_percent}%):
                  </span>
                  <span>-${feeInfo.fee.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>You'll Receive:</span>
                  <span className="text-green-500">${feeInfo.net_amount.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Withdraw Button */}
            <Button
              className="w-full h-12 text-lg"
              onClick={handleWithdraw}
              disabled={loading || !amount || parseFloat(amount) < 20 || parseFloat(amount) > maxWithdraw}
              data-testid="withdraw-button"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
        Withdraw ${amount || '0'} via {selectedMethod === 'cashapp' ? 'Cash App' : 'Venmo'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3">Withdrawal Information</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>✓ All withdrawal methods: 7% processing fee</li>
              <li>✓ Minimum withdrawal: $20</li>
              <li>✓ Maximum withdrawal: $5,000</li>
              <li>✓ Processing time: Usually within 24 hours</li>
              <li>✓ Withdrawals only available after making a real deposit</li>
              <li>✓ All transactions are secure and encrypted</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
